import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";

const DASHBOARD_VIDEO =
  "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/bdqci77w_intro%204.mp4";

export default function LaptopAnimation() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const lidRotate = useTransform(scrollYProgress, [0, 0.3], [0, -8]);
  const smoothLid = useSpring(lidRotate, { stiffness: 60, damping: 20 });

  return (
    <div ref={ref} className="relative w-full max-w-6xl mx-auto laptop-perspective" data-testid="laptop-animation">
      {/* Ambient glow */}
      <div
        className="absolute inset-0 -top-32 -z-10 pointer-events-none"
        aria-hidden
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 60%, rgba(190,221,125,0.35), transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      <motion.div
        className="relative mx-auto"
        style={{ transformStyle: "preserve-3d", rotateX: smoothLid }}
        initial={{ rotateX: 12 }}
        animate={{ rotateX: 0 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 1.8 }}
      >
        {/* LID (screen) */}
        <motion.div
          className="relative w-full"
          style={{
            transformOrigin: "50% 100%",
            transformStyle: "preserve-3d",
          }}
          initial={{ rotateX: -95 }}
          animate={{ rotateX: 0 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          {/* Screen bezel */}
          <div className="relative w-full aspect-[16/10] rounded-t-2xl bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] p-[10px] shadow-[0_20px_60px_rgba(0,0,0,0.7)] border border-[#2a2a2a]">
            {/* Notch/camera */}
            <div className="absolute top-[4px] left-1/2 -translate-x-1/2 w-12 h-1.5 bg-[#050505] rounded-full z-20">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#1a1a1a]" />
            </div>

            {/* Screen content */}
            <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
              <motion.video
                src={DASHBOARD_VIDEO}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.4, delay: 2.0, ease: "easeOut" }}
                data-testid="laptop-dashboard-video"
              />
              {/* Scanline / glow overlay */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-screen"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(190,221,125,0.08) 0%, transparent 40%, transparent 80%, rgba(0,0,0,0.3) 100%)",
                }}
              />
              {/* Reflection highlight */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0, x: "-100%" }}
                animate={{ opacity: [0, 0.5, 0], x: ["-100%", "100%", "100%"] }}
                transition={{ duration: 2, delay: 2.3, ease: "easeInOut" }}
                style={{
                  background:
                    "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)",
                }}
              />
            </div>

            {/* Bottom chin brand */}
            <div className="absolute bottom-0 left-0 right-0 text-center py-1.5">
              <span className="font-display text-[10px] tracking-[0.4em] text-[#BEDD7D]/70 uppercase">
                honor
              </span>
            </div>
          </div>
        </motion.div>

        {/* BASE (keyboard) */}
        <motion.div
          className="relative w-[106%] -ml-[3%] mt-[2px]"
          style={{
            transformOrigin: "50% 0%",
            transformStyle: "preserve-3d",
            transform: "rotateX(85deg) translateZ(-1px)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="relative h-6 rounded-b-[18px] bg-gradient-to-b from-[#2a2a2a] via-[#1a1a1a] to-[#0a0a0a] shadow-[0_40px_80px_rgba(0,0,0,0.9)] border-x border-b border-[#333]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-0.5 bg-[#333] rounded-full" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[3px] bg-[#0a0a0a]" />
          </div>
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-[90%] h-16 bg-black/50 blur-3xl rounded-full" />
        </motion.div>
      </motion.div>

      {/* Floating badges around laptop */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 2.6, duration: 0.8 }}
        className="hidden md:flex absolute -left-6 top-1/4 bg-[#1A2E1A]/90 backdrop-blur-xl border border-[#BEDD7D]/30 rounded-xl px-4 py-3 items-center gap-3 shadow-2xl"
        data-testid="floating-badge-winrate"
      >
        <div className="w-2 h-2 rounded-full bg-[#BEDD7D] animate-pulse" />
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-display">Win Rate</div>
          <div className="text-lg font-bold font-display text-[#BEDD7D]">85%</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 2.8, duration: 0.8 }}
        className="hidden md:flex absolute -right-6 top-1/3 bg-[#1A2E1A]/90 backdrop-blur-xl border border-[#BEDD7D]/30 rounded-xl px-4 py-3 items-center gap-3 shadow-2xl"
        data-testid="floating-badge-traders"
      >
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-display">Traders</div>
          <div className="text-lg font-bold font-display text-[#BEDD7D]">+6.000</div>
        </div>
      </motion.div>
    </div>
  );
}
